function trimTrailingZeros(numStr: string): string {
  return numStr
    .replace(/(\.\d*?[1-9])0+$/u, "$1")
    .replace(/\.0+$/u, "")
    .replace(/\.$/u, "");
}

interface FormattedValue {
  main: string;
  suffix: string;
  extraMain?: string;
  extraSuffix?: string;
}

interface FormatMoneyOptions {
  showSign?: boolean;
  decimals?: number;
}

/* преобразует число в компактный вид */
export function formatMoney(
  value: number,
  options: FormatMoneyOptions = {},
): Omit<FormattedValue, "extraMain" | "extraSuffix"> {
  const { showSign = false, decimals } = options;

  if (isNaN(value) || !isFinite(value)) {
    return { main: "-", suffix: "₽" };
  }

  const sign = value > 0 ? "+" : value < 0 ? "–" : "";
  const displaySign = showSign ? sign : value < 0 ? "–" : "";

  const absoluteValue = Math.abs(value);

  if (absoluteValue >= 1_000_000) {
    return {
      main: `${displaySign}${(absoluteValue / 1_000_000).toFixed(2)}`,
      suffix: "М",
    };
  }
  if (absoluteValue >= 100_000) {
    return {
      main: `${displaySign}${Math.round(absoluteValue / 1000)}`,
      suffix: "К",
    };
  }

  const finalDecimals = decimals ?? (absoluteValue < 100 ? 2 : 0);
  const formattedValue =
    finalDecimals > 0
      ? trimTrailingZeros(absoluteValue.toFixed(finalDecimals))
      : Math.round(absoluteValue).toString();

  return { main: `${displaySign}${formattedValue}`, suffix: "₽" };
}

/* Преобразует число в процент */
export function formatPercent(
  value: number,
  showSign: boolean = false,
): Omit<FormattedValue, "extraMain" | "extraSuffix"> {
  if (isNaN(value) || !isFinite(value)) {
    return { main: "-", suffix: "%" };
  }

  const sign = value > 0 ? "+" : value < 0 ? "–" : "";
  const displaySign = showSign ? sign : "";

  return { main: `${displaySign}${Math.abs(value).toFixed(2)}`, suffix: "%" };
}

/* Преобразует строку или число в number */
const parseNumeric = (v: number | string) =>
  typeof v === "string" ? parseFloat(v.replace(/[\s,₽%]/g, "")) : v;

/* Форматирует число с запятой и нужным количеством знаков */
const formatNumber = (n: number, decimals = 1) =>
  n.toFixed(decimals).replace(".", ",");

/* Форматирует числовые метрики с суффиксами и дополнительными полями */
export function formatMetric(
  value: number | string,
  type: "orders" | "percent" | "currency" | "ratio" = "ratio",
  ordersCount?: number,
): FormattedValue {
  const num = parseNumeric(value);
  if (isNaN(num) || !isFinite(num)) return { main: "-", suffix: "" };

  if (type === "orders") {
    const inThousands = num >= 1000;
    const main = formatNumber(
      inThousands ? num / 1000 : num,
      inThousands ? 1 : 0,
    );
    const suffix = inThousands ? "к" : "₽";

    return ordersCount
      ? {
          main,
          suffix: suffix + "=",
          extraMain: String(ordersCount),
          extraSuffix: "шт",
        }
      : { main, suffix };
  }

  const typeMap: Record<string, [string, string]> = {
    percent: [formatNumber(num), "%"],
    currency: [formatNumber(num), "₽"],
    ratio: [formatNumber(num), ""],
  };

  const [main, suffix] = typeMap[type] ?? typeMap.ratio;
  return { main, suffix };
}

/* Форматирует ISO-дату в читаемый вид по московскому времени */
export function formatDashboardTime(isoString: string) {
  const date = new Date(isoString);
  const moscowTimeZoneOptions: Intl.DateTimeFormatOptions = {
    timeZone: "Europe/Moscow",
  };

  return `${date.toLocaleDateString("ru-RU", { ...moscowTimeZoneOptions, day: "numeric", month: "long" })} в ${date.toLocaleTimeString("ru-RU", { ...moscowTimeZoneOptions, hour: "2-digit", minute: "2-digit" })}`;
}
